// Target data handler: persistence layer for document and study extracts
// Uses localStorage (client cannot write to filesystem)

import { logWarning } from "../../../services/logging/log-handler.js";

function storageKeyFor(type: 'document' | 'study', name: string): string {
  return `${type}_extract_${name}`;
}

function isLegacyGrouper(topic: any): boolean {
  if (!topic || topic.grouper === undefined || topic.grouper === null) {
    return false;
  }

  if (!Array.isArray(topic.grouper)) {
    return true;
  }

  return topic.grouper.some((row: any) => {
    if (!row || typeof row !== 'object') {
      return true;
    }
    const hasRowId = typeof row.rowId === 'string' && row.rowId.trim() !== '';
    const hasLevelsArray = Array.isArray(row.levels);
    return !(hasRowId && hasLevelsArray);
  });
}

function sanitizeExtract(
  type: 'document' | 'study',
  name: string,
  data: any,
  key: string,
  fallbackData: any
): any {
  if (!data || typeof data !== 'object') {
    return JSON.parse(JSON.stringify(fallbackData));
  }

  const topics = Array.isArray(data.topics) ? data.topics : [];
  const hasLegacy = topics.some((topic: any) => isLegacyGrouper(topic));

  if (hasLegacy) {
    logWarning('Detected legacy grouper structure in stored ${type} extract "${name}". Resetting to packaged fallback data.', "target-data-handler", undefined);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(key);
    }
    return JSON.parse(JSON.stringify(fallbackData));
  }

  return data;
}

// Load target extract (document or study) from localStorage or return fallback
export function loadTargetExtract(
  type: 'document' | 'study',
  name: string,
  fallbackData: any
): any {
  const key = storageKeyFor(type, name);
  const existing = typeof window !== 'undefined' ? localStorage.getItem(key) : null;
  if (existing) {
    try {
      const parsed = JSON.parse(existing);
      return sanitizeExtract(type, name, parsed, key, fallbackData);
    } catch {
      // ignore parse error and fall through
    }
  }
  // Return fallback as-is (no preprocessing)
  return JSON.parse(JSON.stringify(fallbackData));
}

// Save target extract to localStorage
export function saveTargetExtract(
  type: 'document' | 'study',
  name: string,
  doc: any
): void {
  const key = storageKeyFor(type, name);
  if (typeof window !== 'undefined') {
    localStorage.setItem(key, JSON.stringify(doc));
  }
}

// Download target extract as JSON file
export function downloadTargetExtract(
  type: 'document' | 'study',
  name: string,
  doc: any
): void {
  const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

